/*
 * File: abbyCadabbyApi.ts
 * Project: rosita
 * -----
 * Last Modified: 03 Mar 2022 12:22:31 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/dist/query/react';
import { Application } from 'models/Application';
import { Division } from 'models/Division';
import { ReduxState } from 'redux/store';

const baseUrl = process.env.REACT_APP_ABBY_CADABBY_BASE_URL || '';
const functionsKey = process.env.REACT_APP_API_HOST_KEY_ABBY_CADABBY || '';

export const abbyCadabbyApi = createApi({
  reducerPath: 'abbyCadabbyApi',
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  }),
  endpoints: (builder) => ({
    getApplication: builder.query<Application, string>({
      query: (applicationId) => ({
        url: `/applications/${applicationId}`
      })
    }),
    getDivision: builder.query<Division, string>({
      query: (divisionId) => ({
        url: `/divisions/${divisionId}`
      })
    }),
    getCurrentDivision: builder.query<Division, void>({
      queryFn: async (_arg, _api, _extra, _baseQuery) => {
        const reduxState = _api.getState() as any;

        if (!reduxState.app.acuityContext) {
          return { error: { error: 'No context' } as FetchBaseQueryError };
        }
        const divisionId = reduxState.app.acuityContext.selectedCustomer.id;

        const result = await _baseQuery({ url: `/divisions/${divisionId}` });

        return result.data ? { data: result.data as Division } : { error: result.error as FetchBaseQueryError };
      }
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getInfo: builder.query<{ version: string; apiName: string }, void>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ): Promise<any> {
        const version = await fetchWithBQ({ url: '/diagnostics/version', responseHandler: (response): Promise<string> => response.text() });
        const apiName = await fetchWithBQ({ url: '/diagnostics/apiName', responseHandler: (response): Promise<string> => response.text() });

        return { data: { version: version.data, apiName: apiName.data } };
      }
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetDivisionQuery, useLazyGetDivisionQuery, useGetCurrentDivisionQuery, useGetVersionQuery, useGetApiNameQuery, useGetApplicationQuery, useGetInfoQuery, useLazyGetInfoQuery } =
  abbyCadabbyApi;
